import { useEffect, type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { toast } from 'sonner'
import { usePermissions } from '@/hooks/usePermission'
import { AccessDenied } from '@/components/shared/AccessDenied'

interface PermissionRouteProps {
  /** Permission codenames required to access this route (resolved to numeric IDs). */
  permissions: string[]
  /** If true, ALL permissions must be present. Default: false (any). */
  requireAll?: boolean
  /**
   * Where to send a user who lacks the permission, instead of rendering the
   * AccessDenied page. Used for write-only routes (e.g. `/templates/create`)
   * whose list page the user CAN read — bouncing them back there with a toast
   * reads better than a dead-end page.
   */
  redirectTo?: string
  /** Toast shown when redirecting (only used with `redirectTo`). */
  deniedMessage?: string
  children: ReactNode
}

function DeniedRedirect({ to, message }: { to: string; message?: string }) {
  useEffect(() => {
    // Fixed id: StrictMode double-runs effects, and a double toast looks broken.
    if (message) toast.error(message, { id: 'permission-route-denied' })
  }, [message])
  return <Navigate to={to} replace />
}

/**
 * Route-level permission guard. Renders children if the user has the required
 * permission(s); otherwise redirects (when `redirectTo` is set) or shows the
 * AccessDenied page.
 */
export function PermissionRoute({
  permissions,
  requireAll = false,
  redirectTo,
  deniedMessage,
  children,
}: PermissionRouteProps) {
  const hasAccess = usePermissions(permissions, requireAll)

  if (!hasAccess) {
    if (redirectTo) return <DeniedRedirect to={redirectTo} message={deniedMessage} />
    return <AccessDenied />
  }

  return <>{children}</>
}
